import React, { cloneElement, useCallback, isValidElement } from 'react';
import { createUseStyles, negative, clsx } from '@pushpay/styles';
import { useResponsiveProp } from '@pushpay/theming';
import { alignHMap } from './alignment';
import { Box } from './box';
import { getLayoutSpaceOrPx } from './helpers';
import { StackItem } from './stack-item';
const useStyles = createUseStyles(theme => ({
  root: ({
    space
  }) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    '&:before': {
      content: '""',
      display: 'block',
      marginTop: negative(getLayoutSpaceOrPx(theme, space))
    }
  }),
  stackItem: ({
    alignItemsH,
    space
  }) => ({
    display: 'flex',
    paddingTop: getLayoutSpaceOrPx(theme, space),
    justifyContent: alignHMap[alignItemsH]
  }),
  stretchStackItem: {
    flexGrow: 1
  }
}));

function isStackItem(maybeStackItem) {
  return maybeStackItem.type === StackItem;
}

export const Stack = ({
  className: classNameProp,
  classes: classesProp,
  component = 'div',
  alignItemsH: alignItemsHProp = 'left',
  spacePx,
  space: spaceProp = 'MEDIUM',
  stretchItem = 'none',
  children,
  ...rest
}) => {
  const alignItemsH = useResponsiveProp(alignItemsHProp);
  const space = useResponsiveProp(spaceProp !== null && spaceProp !== void 0 ? spaceProp : spacePx);
  const classes = useStyles(classesProp, {
    space,
    alignItemsH
  });
  const className = clsx(classNameProp, classes.root);
  const isList = ['ol', 'ul'].includes(component);
  const stackItemComponent = isList ? 'li' : 'div';
  const stackItemCount = React.Children.count(children);
  const shouldStretch = useCallback(index => stretchItem === 'all' || stretchItem === 'first' && index === 0 || stretchItem === 'last' && index === stackItemCount - 1 || typeof stretchItem === 'number' && stretchItem === index || Array.isArray(stretchItem) && stretchItem.includes(index), [stretchItem, stackItemCount]);
  const stackItems = React.Children.map(children, (child, index) => {
    if (!child) {
      return child;
    }

    if (isStackItem(child) && /*#__PURE__*/isValidElement(child)) {
      var _child$props$alignH, _child$props$componen, _child$props$space, _child$props$spacePx, _child$props$stretch;

      // We would normally prefer to use a Context.Provider here rather than cloneElement
      // but the item needs to know its index in the Stack to calculate 'stretch'
      return /*#__PURE__*/cloneElement(child, { ...child.props,
        alignH: (_child$props$alignH = child.props.alignH) !== null && _child$props$alignH !== void 0 ? _child$props$alignH : alignItemsH,
        component: (_child$props$componen = child.props.component) !== null && _child$props$componen !== void 0 ? _child$props$componen : stackItemComponent,
        space: (_child$props$space = child.props.space) !== null && _child$props$space !== void 0 ? _child$props$space : spaceProp,
        spacePx: (_child$props$spacePx = child.props.spacePx) !== null && _child$props$spacePx !== void 0 ? _child$props$spacePx : spacePx,
        stretch: (_child$props$stretch = child.props.stretch) !== null && _child$props$stretch !== void 0 ? _child$props$stretch : shouldStretch(index)
      });
    }

    const stackItemClassName = clsx(classes.stackItem, shouldStretch(index) && classes.stretchStackItem);
    return /*#__PURE__*/React.createElement(Box, {
      component: stackItemComponent,
      space: "NONE",
      className: stackItemClassName
    }, child);
  });
  return /*#__PURE__*/React.createElement(Box, Object.assign({
    component: component,
    space: "NONE",
    className: className
  }, rest), stackItems);
};